/**
 * Interface pour la création d'un département
 */
export interface ISaveDepartment {
  name: string;
  description?: string;
  managerId?: number | null;
}

/**
 * Interface pour la mise à jour d'un département
 */
export interface IUpdateDepartment extends ISaveDepartment {
  id: number;
}

/**
 * Interface pour la réponse API (affichage dans les listes/détails)
 */
export interface IDepartmentResponse {
  id: number;
  name: string;
  description?: string;
  managerId?: number;
  managerLogin?: string;
}

export interface IPermission {
  id: number;
  name: string;
  description?: string;
  createdDate?: string;
  createdBy?: string;
  lastModifiedDate?: string;
  lastModifiedBy?: string;
}

export interface ISavePermission {
  name: string;
  description?: string;
}

/**
 * Interface complète pour la réponse (PermissionDTO)
 */
export interface IPermissionResponse {
  id: number;
  name: string;
  description?: string;
  createdDate?: string; // Instant en Java -> string ISO en JS
  createdBy?: string;
  lastModifiedDate?: string;
  lastModifiedBy?: string;
}

export interface IRole {
  name: string;
  permissions: IPermission[]; // Liste d'objets complets
}

export interface ISaveRole {
  name: string;
  permissions: number[]; // On envoie souvent les IDs des permissions au backend
}

export interface IUser {
  id?: number;
  login?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  activated?: boolean;
  authorities?: string[];
}

export interface ISaveUser extends Omit<IUser, "id" | "activated"> {}

export interface IUpdateUser extends ISaveUser {
  id: number;
}

export interface IChangeStatus {
  id: number;
  enable: boolean;
}

export interface IChangePassword {
  actualPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
}

export interface IChangeCurrentUser {
  firstName?: string;
  lastName?: string;
  login?: string;
}

export enum Gender {
  MALE = "MALE",
  FEMALE = "FEMALE",
}

export enum MaritalStatus {
  SINGLE = "SINGLE",
  MARRIED = "MARRIED",
  DIVORCED = "DIVORCED",
  WIDOWED = "WIDOWED",
}

/**
 * Interface pour la structure simplifiée d'un département dans les réponses DTO
 */
export interface IDepartmentFlatDTO {
  id: number;
  name: string;
}

/**
 * Interface de base correspondant à SaveMemberDTO
 */
export interface ISaveMemberDTO {
  firstName: string;
  lastName: string;
  email?: string | null;
  phoneNumber?: string | null;
  gender?: Gender | null;
  maritalStatus?: MaritalStatus | null;
  marriageDate?: string | null; // Format ISO: YYYY-MM-DD (Optionnel)
  birthDate: string; // Format ISO: YYYY-MM-DD
  joinDate?: string | null; // Format ISO: YYYY-MM-DD
  photoLink?: string | null;
  isChild?: boolean | null;

  // Identifiants des relations
  parentId?: number | null;
  departmentIds?: number[]; // Tableau d'identifiants de départements (ManyToMany)
  ecodimeClassId?: number | null;
}

/**
 * Interface correspondant à UpdateMemberDTO (Hérite de SaveMemberDTO)
 */
export interface IUpdateMemberDTO extends ISaveMemberDTO {
  id: number;
}

/**
 * Interface correspondant à MemberResponseDTO (Données pour l'affichage)
 */
export interface IMemberResponseDTO {
  id: number;
  firstName: string;
  lastName: string;
  gender?: Gender | null;
  maritalStatus?: MaritalStatus | null;
  marriageDate?: string | null; // Format ISO: YYYY-MM-DD
  birthDate: string; // Format ISO: YYYY-MM-DD
  phoneNumber?: string | null;
  email?: string | null;
  joinDate?: string | null; // Format ISO: YYYY-MM-DD
  photoLink?: string | null;
  isChild?: boolean | null;

  // Infos Parent aplaties
  parentId?: number | null;
  parentLastName?: string | null;
  parentFirstName?: string | null;

  // Liste des départements associés
  departments?: IDepartmentFlatDTO[];

  // Infos Ecodime Class aplaties
  ecodimeClassId?: number | null;
  ecodimeClassName?: string | null;

  // Données d'audit
  createdDate?: string | null; // Format ISO Instant
  createdBy?: string | null;
  lastModifiedDate?: string | null; // Format ISO Instant
  lastModifiedBy?: string | null;
}

/**
 * Interface pour la réponse paginée globale du serveur (si applicable)
 */
export interface IMemberPageResponse {
  data: IMemberResponseDTO[];
  totalCount: number;
}

export interface IContributionTypeResponse {
  id: number;
  name: string;
  fundId: number;
  fundName: string;
  createdDate?: string;
  createdBy?: string;
  lastModifiedDate?: string;
  lastModifiedBy?: string;
}

export interface ISaveContributionType {
  name: string;
  fundId?: number | null;
}

export interface IUpdateContributionType extends ISaveContributionType {
  id: number;
}

/**
 * Interface pour la création d'une présence
 */
export interface ISaveAttendance {
  present: boolean;
  remarks?: string | null;
  memberId: number;
  eventId: number;
}

/**
 * Interface pour la mise à jour d'une présence
 */
export interface IUpdateAttendance extends ISaveAttendance {
  id: number;
}

/**
 * Interface pour la réponse API Présence (avec infos dénormalisées pour l'affichage)
 */
export interface IAttendanceResponse {
  id: number;
  present: boolean;
  remarks?: string | null;

  // Relations aplaties
  memberId: number;
  memberLastName: string;
  eventId: number;
  eventTitle: string;

  // Traces d'audit
  createdDate?: string | null;
  createdBy?: string | null;
  lastModifiedDate?: string | null;
  lastModifiedBy?: string | null;
}

/**
 * Interface pour la lecture (Réponse du serveur)
 */
export interface IContributionResponse {
  id: number;
  amount: number;
  paymentDate: string;
  observation?: string;
  createdDate?: string;
  createdBy?: string;
  memberId: number;
  memberFullName: string;
  typeId: number;
  typeName: string;
  departmentId?: number;
  departmentName?: string;
}

/**
 * Interface pour la création (POST)
 */
export interface ISaveContribution {
  amount: number;
  paymentDate: string;
  observation?: string;
  memberId: number;
  typeId: number;
}

/**
 * Interface pour la mise à jour (PUT)
 */
export interface IUpdateContribution {
  id: number;
  amount: number;
  paymentDate: string;
  observation?: string;
  memberId: number;
  typeId: number;
}

export interface ISaveOffering {
  amount: number;
  offeringDate: string; // Format ISO string
  description?: string | null;
  eventId: number;
  fundId?: number | null;
}

export interface IUpdateOffering extends ISaveOffering {
  id: number;
}

export interface IOfferingResponse {
  id: number;
  amount: number;
  offeringDate: string;
  description?: string | null;
  fundId: number;
  fundName: string;
  createdDate?: string | null;
  createdBy?: string | null;
  eventId: number;
  eventTitle?: string | null;
}

export interface ISaveExpense {
  title: string;
  amount: number;
  expenseDate: string;
  category?: string | null;
  description?: string | null;
  approvedById?: number | null;
  fundId?: number | null;
}

export interface IUpdateExpense extends ISaveExpense {
  id: number;
}

export interface IExpenseResponse {
  id: number;
  title: string;
  amount: number;
  expenseDate: string;
  category?: string | null;
  description?: string | null;
  fundId: number;
  fundName: string;
  createdDate?: string | null;
  createdBy?: string | null;
  approvedById?: number | null;
  approvedByLogin?: string | null;
}

// EventType
export interface IEventTypeResponse {
  id: number;
  name: string;
  createdDate?: string | null;
  createdBy?: string | null;
  lastModifiedDate?: string | null;
  lastModifiedBy?: string | null;
}

export interface ISaveEventType {
  name: string;
}

export interface IUpdateEventType extends ISaveEventType {
  id: number;
}

// Représente EventResponseDTO
export interface IEventResponse {
  id: number;
  title?: string | null;
  eventDate: string; // Stocké sous forme de chaîne ISO (Instant)
  description?: string | null;
  eventTypeId: number;
  eventTypeName: string;
  createdDate?: string | null;
  createdBy?: string | null;
  lastModifiedDate?: string | null;
  lastModifiedBy?: string | null;
}

// Représente SaveEventDTO
export interface ISaveEvent {
  title?: string | null;
  eventDate: string;
  description?: string | null;
  eventTypeId: number;
}

// Représente UpdateEventDTO
export interface IUpdateEvent extends ISaveEvent {
  id: number;
}

/**
 * Interface représentant le DTO de réponse pour une Dîme (Tithe)
 */
export interface ITitheResponseDTO {
  id: number;
  amount: number;
  titheDate: string;
  description?: string | null;
  fundId: number;
  fundName: string;
  createdDate?: string;
  createdBy?: string;
  lastModifiedDate?: string;
  lastModifiedBy?: string;
}

export interface ISaveTitheDTO {
  amount: number;
  titheDate: string;
  description?: string | null;
  fundId?: number | null;
}

export interface IUpdateTitheDTO extends ISaveTitheDTO {
  id: number;
}

export interface IEcodimeClassResponseDTO {
  id: number;
  name: string;
  ageMin?: number | null;
  ageMax?: number | null;
  createdDate?: string;
  createdBy?: string;
  lastModifiedDate?: string;
  lastModifiedBy?: string;
}

export interface ISaveEcodimeClassDTO {
  name: string;
  ageMin?: number | null;
  ageMax?: number | null;
}

export interface IUpdateEcodimeClassDTO extends ISaveEcodimeClassDTO {
  id: number;
}

/**
 * DTOs formation
 */
export interface ISaveTrainingDTO {
  title: string;
  description?: string;
  startDate: string;
  endDate?: string;
  instructor?: string;
}

export interface IUpdateTrainingDTO extends ISaveTrainingDTO {
  id: number;
}

export interface ITrainingResponseDTO {
  id: number;
  title: string;
  description?: string;
  startDate: string;
  endDate?: string;
  instructor?: string;
  createdDate?: string;
  createdBy?: string;
  lastModifiedDate?: string;
  lastModifiedBy?: string;
}

/**
 * DTO utilisé pour la création d'un suivi de formation
 */
export interface ISaveMemberTrainingDTO {
  completed: boolean;
  grade?: string;
  certificateIssued?: boolean;
  completionDate?: string | null; // Transmis sous forme de chaîne ISO (Instant)
  memberId: number;
  trainingId: number;
}

export interface IUpdateMemberTrainingDTO extends ISaveMemberTrainingDTO {
  id: number;
}

export interface IMemberTrainingResponseDTO {
  id: number;
  completed: boolean;
  grade?: string;
  certificateIssued?: boolean;
  completionDate?: string;

  // Informations du Membre aplaties
  memberId: number;
  memberLastName: string;
  memberFirstName: string;

  // Informations de la Formation aplaties
  trainingId: number;
  trainingTitle: string;

  // Bloc d'audit (AbstractAuditingEntity)
  createdDate?: string;
  createdBy?: string;
  lastModifiedDate?: string;
  lastModifiedBy?: string;
}

/**
 * Interface pour la création d'un fonds/caisse
 */
export interface ISaveFund {
  name: string;
  description?: string;
  official: boolean;
  active: boolean;
}

/**
 * Interface pour la mise à jour d'un fonds/caisse
 */
export interface IUpdateFund extends ISaveFund {
  id: number;
}

/**
 * Interface pour la réponse API d'un fonds/caisse (avec audit)
 */
export interface IFundResponse {
  id: number;
  name: string;
  description?: string;
  official: boolean;
  active: boolean;
  createdBy?: string;
  createdDate?: string;
  lastModifiedBy?: string;
  lastModifiedDate?: string;
}

export enum AuditActionType {
  CREATE = "CREATE",
  UPDATE = "UPDATE",
  DELETE = "DELETE",
  ACTIVE = "ACTIVE",
  DEACTIVATE = "DEACTIVATE",
  ARCHIVE = "ARCHIVE",
  UNARCHIVE = "UNARCHIVE",
  VIEW = "VIEW",
  LOGIN = "LOGIN",
  LOGOUT = "LOGOUT",
  EXPORT = "EXPORT",
  DOWNLOAD = "DOWNLOAD",
  VALIDATE = "VALIDATE",
  REJECT = "REJECT",
  APPROVE = "APPROVE",
  OTHER = "OTHER",
}

export enum AuditLogStatus {
  SUCCESS = "SUCCESS",
  FAILED = "FAILED",
}

export interface IAuditLogResponseDTO {
  id: number;
  userId: string;
  fullName: string | null;
  actionType: AuditActionType;
  description: string;
  oldValue: string | null;
  newValue: string | null;
  ipAddress: string | null;
  browser: string | null;
  status: AuditLogStatus;
  actionDate: string;
}

// ==========================================
// ENUM : RESOURCE TYPE
// ==========================================
export enum ResourceType {
  FUND = "FUND",
  TRAINING = "TRAINING",
  EVENT = "EVENT",
}

// ==========================================
// ENTITÉ : RESOURCE PERMISSION
// ==========================================

export interface ISaveResourcePermission {
  resourceType: ResourceType;
  resourceId: number;
  canRead: boolean;
  canWrite: boolean;
  canDelete: boolean;
  canApprove: boolean;
  userId: number;
}

export interface IUpdateResourcePermission extends ISaveResourcePermission {
  id: number;
}

export interface IResourcePermissionResponse {
  id: number;
  resourceType: ResourceType;
  resourceId: number;
  resourceName: string;
  canRead: boolean;
  canWrite: boolean;
  canDelete: boolean;
  canApprove: boolean;
  userId: number;
  userLogin: string;
  createdBy?: string;
  createdDate?: string;
  lastModifiedBy?: string;
  lastModifiedDate?: string;
}

/**
 * Interface pour la demande de réinitialisation de mot de passe (RequestPasswordResetDTO)
 */
export interface IRequestPasswordReset {
  mail: string;
}

/**
 * Interface pour la validation du nouveau mot de passe avec la clé (KeyAndPasswordDTO)
 */
export interface IKeyAndPassword {
  key: string;
  newPassword: string;
}

// Mapping pour afficher des badges colorés selon le type d'action
export const AUDIT_ACTION_LABELS: Record<
  AuditActionType,
  { label: string; colorClass: string }
> = {
  [AuditActionType.CREATE]: {
    label: "Création",
    colorClass: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  },
  [AuditActionType.UPDATE]: {
    label: "Modification",
    colorClass: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  },
  [AuditActionType.DELETE]: {
    label: "Suppression",
    colorClass: "bg-rose-500/10 text-rose-400 border-rose-500/20",
  },
  [AuditActionType.ACTIVE]: {
    label: "Activation",
    colorClass: "bg-cyan-500/10 text-cyan-400 border-cyan-500/20",
  },
  [AuditActionType.DEACTIVATE]: {
    label: "Désactivation",
    colorClass: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  },
  [AuditActionType.ARCHIVE]: {
    label: "Archivage",
    colorClass: "bg-slate-500/10 text-slate-400 border-slate-700",
  },
  [AuditActionType.UNARCHIVE]: {
    label: "Désarchivage",
    colorClass: "bg-indigo-500/10 text-indigo-400 border-indigo-500/20",
  },
  [AuditActionType.VIEW]: {
    label: "Consultation",
    colorClass: "bg-purple-500/10 text-purple-400 border-purple-500/20",
  },
  [AuditActionType.LOGIN]: {
    label: "Connexion",
    colorClass: "bg-teal-500/10 text-teal-400 border-teal-500/20",
  },
  [AuditActionType.LOGOUT]: {
    label: "Déconnexion",
    colorClass: "bg-zinc-500/10 text-zinc-400 border-zinc-700",
  },
  [AuditActionType.EXPORT]: {
    label: "Export",
    colorClass: "bg-pink-500/10 text-pink-400 border-pink-500/20",
  },
  [AuditActionType.DOWNLOAD]: {
    label: "Téléchargement",
    colorClass: "bg-violet-500/10 text-violet-400 border-violet-500/20",
  },
  [AuditActionType.VALIDATE]: {
    label: "Validation",
    colorClass: "bg-green-500/10 text-green-400 border-green-500/20",
  },
  [AuditActionType.REJECT]: {
    label: "Rejet",
    colorClass: "bg-red-500/10 text-red-400 border-red-500/20",
  },
  [AuditActionType.APPROVE]: {
    label: "Approbation",
    colorClass: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  },
  [AuditActionType.OTHER]: {
    label: "Autre",
    colorClass: "bg-slate-500/10 text-slate-400 border-slate-700",
  },
};

// Mapping pour le statut Succès / Échec
export const AUDIT_STATUS_LABELS: Record<
  AuditLogStatus,
  { label: string; colorClass: string; icon: string }
> = {
  [AuditLogStatus.SUCCESS]: {
    label: "Succès",
    colorClass: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    icon: "fas fa-check-circle",
  },
  [AuditLogStatus.FAILED]: {
    label: "Échec",
    colorClass: "bg-rose-500/10 text-rose-400 border-rose-500/20",
    icon: "fas fa-times-circle",
  },
};
